import { useCallback, useEffect, useMemo, useRef } from "react";

import { defaultMST1010_R01Filter, useMST1010_R01Store } from "src/containers/mes/MST/MST1010_R01/Store_MST1010_R01";
import useUiuxStore from "src/store/useUiuxStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { createMST1010_R01, deleteMST1010_R01, downloadExcel_MST1010_R01, getMST1010_R01AllCount, getMST1010_R01Image, getMST1010_R01List, getMST1010_R01One, patchMST1010_R01 } from "./Api_MST1010_R01";
import { defaultMST1010_R01Dto, MST1010_R01Dto } from "./Dto_MST1010_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { defaultItem, ItemDto } from "src/dtos/item/itemDto";
import { validateCustomCd } from "src/utils/grid/GridValidateContent";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	MST1010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST1010_R01Dto> | null>;
	containerRef: React.RefObject<HTMLDivElement | null>;
	
}

interface MST1010_R01DropdownOption {
	minorCd: string;
}

// initLabelLess 드롭다운은 현재 값이 비어 있거나 옵션 목록에 없어도 첫 옵션이 화면에 표시될 수 있다.
// 저장값을 화면에 보이는 옵션과 맞추되, 공통코드가 아직 로드되지 않았다면 기존 값을 유지한다.
const getMST1010_R01DropdownValue = (
	currentValue: string,
	optionList: readonly MST1010_R01DropdownOption[],
) => {
	if (optionList.length === 0) return currentValue;

	const hasCurrentValue = optionList.some((option) => option.minorCd === currentValue);
	return hasCurrentValue ? currentValue : optionList[0].minorCd;
};

export const Content_MST1010_R01 = (props: Props) => {

	const {
		gridRef,
		containerRef,
		
	} = props;

	useFetchActionbarAuth("MST1010_R01");
	const { unitList, brandCdList, itemGroupList, itemAccountList, prodStockTypeList, yesNoList, customCdList, fetchItemCdCode } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	
	const { 
		
		MST1010_R01List, setMST1010_R01List, 
		selectedMST1010_R01,  setSelectedMST1010_R01, 
		editMST1010_R01, setEditMST1010_R01, 
		allCount, setAllCount,
		initData,
		setScrollTop,
		} = useMST1010_R01Store();

	const { 
		MST1010_R01Filter, setMST1010_R01Filter,
		MST1010_R01PastFilter, setMST1010_R01PastFilter,
	 } = useMST1010_R01Store();
	const { setMessage } = useUiuxStore();
	const isResizing = useRef(true);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_MST1010_R01(
			'',
			getParams(MST1010_R01Filter),
			MST1010_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '품목 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [MST1010_R01Filter, MST1010_R01List, setLoadingSpinner]);

	const getParams = (filter: { brandCd: string, itemGroup: string, itemAccount: string, itemCd: string, itemNm: string }) => {
		const params = {
			itemBrand: filter.brandCd,
			itemGroup: filter.itemGroup,
			itemAccount: filter.itemAccount,
			itemCd: filter.itemCd,
			itemNm: filter.itemNm,
			listLength: 0
		}
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getMST1010_R01AllCount(getParams(MST1010_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [MST1010_R01Filter])

	const defaultMST1010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST1010_R01_ROW_VALIDATE, {
				brandCdList,
				itemAccountList,
				itemGroupList,
				prodStockTypeList,
				unitList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultMST1010_R01Dto };
		}

		return {
			...defaultMST1010_R01Dto,
			brandCd: brandCdList[0].minorCd,
			itemGroup: itemGroupList[0].minorCd,
			itemAccount: itemAccountList[0].minorCd,
			itemUnit: unitList[0].minorCd,
			prodStockType: prodStockTypeList.find((value) => value.minorCd === 'I')?.minorCd || 'I',
			inspectYn: yesNoList.find((value) => value.minorCd === 'Y')?.minorCd || 'Y',
			inserted: true,
		};
	}, [
		defaultMST1010_R01Dto, brandCdList, itemGroupList, itemAccountList, prodStockTypeList, unitList, yesNoList,
		validateCommonCodeLists,
	]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const response = await getMST1010_R01List(
			'search',
			getParams(MST1010_R01Filter),
			MST1010_R01List.length ?? 0
		);
		let newList = [...response.data.data ] as MST1010_R01Dto[];
		setMST1010_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedMST1010_R01,
				() => true,// () => !newList.find(value => value.itemCd === selectedMST1010_R01?.itemCd)
			);
			setMST1010_R01PastFilter(MST1010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			handleCreateClick();
			setSelectedMST1010_R01(undefined);
		}
		setLoadingSpinner(false);

		return response.data.data;
	}, [MST1010_R01Filter, MST1010_R01List, gridRef, editMST1010_R01, setMST1010_R01List, setSelectedMST1010_R01, setScrollTop])

	const fetchOne = useCallback(async () => {
		if (!selectedMST1010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getMST1010_R01One(selectedMST1010_R01.itemCd);
			const defaultRow = defaultMST1010_R01Row();
			const fetchedItem = response.data.data;
			setEditMST1010_R01({
				...defaultRow,
				...fetchedItem,
				prodStockType: fetchedItem.prodStockType || defaultRow.prodStockType,
				inspectYn: fetchedItem.inspectYn || defaultRow.inspectYn,
			});
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [selectedMST1010_R01]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleCreateClick = useCallback(async () => {
		initItemData();
	}, []);

	const handleDeleteClick = useCallback(async () => {
		if (!selectedMST1010_R01) {
			showWarning('선택된 품목이 없습니다.'); return;
		}
		if (window.confirm('정말로 삭제하시겠습니까?')) {
			await deleteMST1010_R01(selectedMST1010_R01.itemCd);
			refetchUpdateList('delete', selectedMST1010_R01, selectedMST1010_R01, 'itemCd', MST1010_R01List, setMST1010_R01List);
			handleCreateClick();
			showSuccess(`${selectedMST1010_R01.itemCd} 품목 삭제가 완료되었습니다.`)
		}

	}, [selectedMST1010_R01])

	const validateInsertData = (data: MST1010_R01Dto) => {

		if (!validateString(data.itemCd)) {
			showWarning("품목코드를 입력하세요."); return false;
		}
		if (!validateString(data.itemNm)) {
			showWarning("품명을 입력하세요."); return false;
		}
		if (!validateString(data.itemSpec)) {
			showWarning("규격을 입력하세요."); return false;
		}
		if (!validateString(data.modelNo)) {
			showWarning("모델명을 입력하세요."); return false;
		}
		if (!validateString(data.prodStockType)) {
			showWarning("생산재고 발생을 선택하세요."); return false;
		}
		if (!validateString(data.inspectYn)) {
			showWarning("품질검사 필수 여부를 선택하세요."); return false;
		}
		// if (!validateString(data.customCd)) {
		// 	showWarning("거래처코드를 입력하세요."); return false;
		// }
		// if (!validateCustomCd(data.customCd, customCdList)) {
		// 	showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		// }
		// if (!validateString(data.customItemcd)) {
		// 	showWarning("발주처 제품코드를 입력하세요."); return false;
		// }

		return true;
	}

	// 사용자가 드롭다운을 직접 변경하지 않아도 화면에 표시된 옵션 코드가 저장되도록 Payload를 보정한다.
	const buildMST1010_R01SaveData = useCallback((data: MST1010_R01Dto): MST1010_R01Dto => ({
		...data,
		brandCd: getMST1010_R01DropdownValue(data.brandCd, brandCdList),
		itemUnit: getMST1010_R01DropdownValue(data.itemUnit, unitList),
		itemGroup: getMST1010_R01DropdownValue(data.itemGroup, itemGroupList),
		itemAccount: getMST1010_R01DropdownValue(data.itemAccount, itemAccountList),
		prodStockType: getMST1010_R01DropdownValue(data.prodStockType, prodStockTypeList),
		inspectYn: getMST1010_R01DropdownValue(data.inspectYn, yesNoList),
	}), [brandCdList, unitList, itemGroupList, itemAccountList, prodStockTypeList, yesNoList]);

	const handleSaveClick = useCallback(async () => {

		if (!editMST1010_R01) {
			showWarning("품목을 선택하세요."); return;
		}

		const saveData = buildMST1010_R01SaveData(editMST1010_R01);
		if (!validateInsertData(saveData)) return;

		let processName = '';
		let responseData: ItemDto = { ...defaultItem };
	
		if (selectedMST1010_R01) {
			processName = 'patch';
			const response = await patchMST1010_R01(saveData);
			responseData = response.data.data;
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${saveData.itemCd} 품목 수정이 완료되었습니다.`);
					setMessage(`${saveData.itemCd} 품목 수정이 완료되었습니다.`);
				}else return;
			}
		} else {
			processName = 'post';
			const response = await createMST1010_R01(saveData);
			responseData = response.data.data;
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${saveData.itemCd} 품목 등록이 완료되었습니다.`);
					setMessage(`${saveData.itemCd} 품목 등록이 완료되었습니다.`);
				}else return;
			}
		}
	
		setTimeout(() => {
			setMessage("");
		}, 5000)
		if(editMST1010_R01) {
			refetchUpdateList(processName, responseData, saveData, 'itemCd', MST1010_R01List, setMST1010_R01List);
			setSelectedMST1010_R01(responseData);
		}
		await fetchItemCdCode();

	}, [editMST1010_R01, selectedMST1010_R01, buildMST1010_R01SaveData])
	
	const initItemData = () => {
		setSelectedMST1010_R01(undefined);
		setEditMST1010_R01(defaultMST1010_R01Row());
		setScrollTop(null);
	}

	//selectedMST1010_R01 변경 시 품목 이미지 불러오기
	useEffect(() => {
		if (selectedMST1010_R01) {
			fetchOne();
		}else{
			setEditMST1010_R01(defaultMST1010_R01Row());
		}
	}, [selectedMST1010_R01])

	return {
		handleExcelClick,
		defaultMST1010_R01Row,
		fetchData,
		initContainerData,
		handleCreateClick,
		handleDeleteClick,
		handleSaveClick,
	}
}
